/**
 * PublicLayout — anonymous MVP shell: header nav + inline search + outlet.
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Search, MapPin, CalendarDays, Store, Landmark, X, Menu } from 'lucide-react';
import { searchPublic } from '@/services/api/public';
import { getPublicConfig } from '@/services/api/config';
import { PUBLIC_IDS } from '@/constants/testIds';
import symbolMulti from '@/assets/brand/vivilazona-symbol.svg';
import wordmarkWhite from '@/assets/brand/vivilazona-wordmark-white.svg';

const NAV = [
	{ to: '/eventi', label: 'Eventi', icon: CalendarDays },
	{ to: '/luoghi', label: 'Luoghi', icon: Landmark },
	{ to: '/attivita', label: 'Attività', icon: Store },
	{ to: '/mappa', label: 'Mappa', icon: MapPin },
];

export function PublicLayout() {
	const [q, setQ] = useState('');
	const [res, setRes] = useState(null);
	const [open, setOpen] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const navigate = useNavigate();
	const timer = useRef(null);
	const { data: cfg } = useQuery({ queryKey: ['public-config'], queryFn: getPublicConfig });

	useEffect(() => {
		if (timer.current) clearTimeout(timer.current);
		if (q.trim().length < 2) {
			setRes(null);
			return;
		}
		timer.current = setTimeout(async () => {
			try {
				setRes(await searchPublic(q));
				setOpen(true);
			} catch {
				setRes(null);
			}
		}, 250);
		return () => timer.current && clearTimeout(timer.current);
	}, [q]);

	const go = (path) => {
		setOpen(false);
		setMenuOpen(false);
		setQ('');
		navigate(path);
	};

	const groups = res
		? [
				['Eventi', 'eventi', res.events, (e) => e.title],
				['Luoghi', 'luoghi', res.places, (p) => p.name],
				['Attività', 'attivita', res.activities, (a) => a.title],
		  ].filter(([, , arr]) => arr && arr.length)
		: [];

	return (
		<div className="min-h-screen bg-[#0b1120] text-slate-100" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
			<header className="sticky top-0 z-40 border-b border-slate-800 bg-[#0b1120]/90 backdrop-blur">
				<div className="mx-auto max-w-6xl px-5 py-3">
					<div className="flex flex-wrap items-center gap-x-3 gap-y-3">
						<Link to="/" data-testid={PUBLIC_IDS.brand} className="order-1 flex items-center" aria-label="ViviLaZona — home">
							<span className="sr-only">ViviLaZona</span><img src={symbolMulti} alt="" aria-hidden="true" className="h-9 w-auto sm:h-10" /><img src={wordmarkWhite} alt="ViviLaZona" className="ml-2.5 h-5 w-auto sm:h-6" />
						</Link>
						<nav className="order-2 ml-4 hidden items-center gap-1 md:flex">
							{NAV.map(({ to, label, icon: Icon }) => (
								<NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
									className={({ isActive }) => `flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${isActive ? 'bg-emerald-400/10 text-emerald-300' : 'text-slate-300 hover:text-emerald-300'}`}>
									<Icon size={15} /> {label}
								</NavLink>
							))}
						</nav>
						<button type="button" data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={menuOpen}
							onClick={() => setMenuOpen((v) => !v)}
							className="order-2 ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-700 text-slate-200 hover:border-emerald-400/40 hover:text-emerald-300 md:hidden">
							{menuOpen ? <X size={18} /> : <Menu size={18} />}
						</button>
						<div className="relative order-5 w-full basis-full md:order-3 md:ml-auto md:w-auto md:max-w-xs md:basis-auto">
							<Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
							<input
								value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
								data-testid={PUBLIC_IDS.searchInput}
								placeholder="Cerca eventi, luoghi, attività…"
								className="w-full rounded-full border border-slate-700 bg-slate-900/60 py-1.5 pl-9 pr-8 text-sm text-slate-100 outline-none focus:border-emerald-400/50"
							/>
							{q && <button onClick={() => { setQ(''); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500"><X size={14} /></button>}
							{open && res && (
								<div data-testid={PUBLIC_IDS.searchResults} className="absolute right-0 z-50 mt-2 max-h-80 w-full overflow-auto rounded-xl border border-slate-700 bg-slate-900 p-2 shadow-2xl md:w-80">
									{groups.length === 0 && <p className="px-2 py-3 text-sm text-slate-500">Nessun risultato.</p>}
									{groups.map(([label, base, arr, title]) => (
										<div key={base} className="mb-1">
											<p className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</p>
											{arr.map((it) => (
												<button key={it.id} onClick={() => go(`/${base}/${it.slug}`)} className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-slate-200 hover:bg-slate-800">
													{title(it)}
												</button>
											))}
										</div>
									))}
								</div>
							)}
						</div>
						<Link to="/login" className="order-4 hidden shrink-0 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-emerald-400/40 hover:text-emerald-300 md:block">
							Admin
						</Link>
					</div>
					{menuOpen && (
						<nav data-testid="mobile-menu" className="mt-3 grid gap-1 border-t border-slate-800 pt-3 md:hidden">
							{NAV.map(({ to, label, icon: Icon }) => (
								<NavLink key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}`} onClick={() => setMenuOpen(false)}
									className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${isActive ? 'bg-emerald-400/10 text-emerald-300' : 'text-slate-200 hover:text-emerald-300'}`}>
									<Icon size={16} /> {label}
								</NavLink>
							))}
							<Link to="/login" data-testid="mobile-nav-admin" onClick={() => setMenuOpen(false)}
								className="mt-1 flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-slate-400 hover:text-emerald-300">
								Admin
							</Link>
						</nav>
					)}
				</div>
			</header>
			<Outlet />
			<p data-testid="demo-disclaimer" className="border-t border-slate-800 bg-slate-900/40 px-5 py-3 text-center text-xs text-slate-400">
				Versione dimostrativa: contenuti e immagini a scopo illustrativo, non ufficiali e soggetti a verifica.
			</p>
			<footer data-testid="public-footer" className="border-t border-slate-800 py-10 text-center text-xs text-slate-500">
				<div className="mx-auto max-w-6xl px-5">
					<p className="text-slate-400">{cfg?.name || 'ViviLaZona'}</p>
					{cfg?.contacts?.email && <p className="mt-1">{cfg.contacts.email}</p>}
					{(cfg?.social?.facebook || cfg?.social?.instagram || cfg?.social?.youtube) && (
						<div className="mt-2 flex justify-center gap-3">
							{cfg.social.facebook && <a href={cfg.social.facebook} target="_blank" rel="noreferrer" className="hover:text-emerald-300">Facebook</a>}
							{cfg.social.instagram && <a href={cfg.social.instagram} target="_blank" rel="noreferrer" className="hover:text-emerald-300">Instagram</a>}
							{cfg.social.youtube && <a href={cfg.social.youtube} target="_blank" rel="noreferrer" className="hover:text-emerald-300">YouTube</a>}
						</div>
					)}
					{(cfg?.legal?.privacy_url || cfg?.legal?.cookie_url || cfg?.legal?.legal_notes_url) && (
						<div className="mt-2 flex justify-center gap-3">
							{cfg.legal.privacy_url && <a href={cfg.legal.privacy_url} target="_blank" rel="noreferrer" className="hover:text-emerald-300">Privacy</a>}
							{cfg.legal.cookie_url && <a href={cfg.legal.cookie_url} target="_blank" rel="noreferrer" className="hover:text-emerald-300">Cookie</a>}
							{cfg.legal.legal_notes_url && <a href={cfg.legal.legal_notes_url} target="_blank" rel="noreferrer" className="hover:text-emerald-300">Note legali</a>}
						</div>
					)}
					<p className="mt-3 text-slate-600">ViviLaZona · Tutto il territorio, in un unico posto.</p>
				</div>
			</footer>
		</div>
	);
}

export default PublicLayout;
